import * as React from 'react';

import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import type {GetClosedLeadFollowupAutomation_closedLeadFollowupAutomation as ClosedLeadFollowupAutomation} from 'nutshell-graphql-types';

import {
    convertTagsToOptions,
    CreateLeadFormFields,
    getAssigneeId,
} from '../../core/form/fields/create-lead-form-fields';
import type {Option} from '../../core/select/graphql-select-tags';
import {SalesAutomationSidebarFormWrapper} from '../../core/sidebar/sales-automation-sidebar-form-wrapper';

export type FormData = {
    pipelineId: string;
    assigneeId: string;
    tags: string[] | null | undefined;
};

type Props = {
    formData: ClosedLeadFollowupAutomation | null | undefined;
    status: string;
    getFormSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => void;
    onSubmit: (formData: FormData) => Promise<any>;
    onDelete?: (entityId: string) => Promise<any>;
};

export function ClosedLeadFollowupRuleForm(props: Props) {
    const [tags, setTags] = React.useState<Option[]>(
        (props.formData && convertTagsToOptions(props.formData.tags)) || []
    );
    const [pipelineId, setPipelineId] = React.useState<string | null | undefined>(
        props.formData && props.formData.newStageset.id
    );
    const [assigneeId, setAssigneeId] = React.useState<string | null | undefined>(
        props.formData && getAssigneeId(props.formData.assigneeOrRule)
    );
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(undefined);

    const handleSaveForm = () => {
        setErrorMessage(undefined);
        if (pipelineId && assigneeId) {
            return props.onSubmit({
                pipelineId,
                assigneeId,
                tags: tags.map((tag) => tag.id),
            });
        } else if (!pipelineId) {
            setErrorMessage('Please select a pipeline');
        } else if (!assigneeId) {
            setErrorMessage('Please select an assignee');
        }
    };

    const getStatusText = () => {
        switch (props.status) {
            case 'CANCELLED':
                return 'cancelled';
            case 'WON':
                return 'won';
            case 'LOST':
                return 'lost';
        }
    };

    return (
        <SalesAutomationSidebarFormWrapper
            id='closed-lead-followup-rule-form'
            onSaveForm={handleSaveForm}
            onDelete={
                props.onDelete
                    ? {
                          deleteText: 'Remove follow-up rule',
                          deleteConfirmationTitle:
                              'Are you sure you want to remove this follow-up rule?',
                          onDeleteConfirm: props.onDelete,
                      }
                    : undefined
            }
            errorMessage={errorMessage || undefined}
        >
            <Stack spacing={32}>
                <Stack spacing={16}>
                    <Body color='light'>
                        Create a new lead automatically when a lead in this pipeline is marked as{' '}
                        {getStatusText()}.
                    </Body>
                    <Body color='light'>
                        The new lead will automatically inherit the people, companies, tags, and
                        products from the original lead.
                    </Body>
                </Stack>
                <CreateLeadFormFields
                    pipelineId={pipelineId}
                    assigneeId={assigneeId}
                    tags={tags}
                    onPipelineChange={setPipelineId}
                    onAssigneeChange={setAssigneeId}
                    onTagsChange={setTags}
                />
            </Stack>
            <CrossBrowserHiddenFormSubmit inputRef={props.getFormSubmitBtnRef} />
        </SalesAutomationSidebarFormWrapper>
    );
}
